import type { SubBatch } from '../../hooks/usePipelineSnapshot';

// Section 4 of the spec: one block per active sub-batch within its current
// phase column. Fill bar uses succeeded / product_count for the active phase.

interface Props {
  sb: SubBatch;
  progressed?: boolean; // true for 2s after a counter increment (pulse glow)
}

const PROVIDER_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  openai: { bg: '#1a1a1a', fg: '#ffffff', label: 'OpenAI' },
  vertexai: { bg: '#4285F4', fg: '#ffffff', label: 'Vertex' },
};

function fillFor(sb: SubBatch): { numerator: number; denominator: number; provider: string | null } {
  const phase = sb.currentPhase;
  if (phase === 'enrichment') {
    return { numerator: sb.enrichment.succeeded || 0, denominator: sb.productCount, provider: sb.enrichment.provider || null };
  }
  if (phase === 'benefit_scoring') {
    return { numerator: sb.benefitScoring.succeeded || 0, denominator: sb.productCount, provider: null };
  }
  if (phase === 'embedding') {
    return { numerator: sb.embedding.succeeded || 0, denominator: sb.productCount, provider: sb.embedding.provider || null };
  }
  if (phase === 'aeo_generation') {
    return { numerator: sb.aeoGeneration.generated || 0, denominator: sb.productCount, provider: sb.aeoGeneration.provider || null };
  }
  if (phase === 'aeo_validation') {
    return { numerator: sb.aeoValidation.validated || 0, denominator: sb.productCount, provider: sb.aeoGeneration.provider || null };
  }
  if (phase === 'waiting_for_cohort') {
    return { numerator: sb.embedding.succeeded || 0, denominator: sb.productCount, provider: null };
  }
  return { numerator: 0, denominator: sb.productCount, provider: null };
}

function phaseStartedAt(sb: SubBatch): string | null {
  switch (sb.currentPhase) {
    case 'enrichment': return sb.enrichment.startedAt;
    case 'benefit_scoring': return sb.benefitScoring.startedAt;
    case 'embedding': return sb.embedding.startedAt;
    case 'aeo_generation': return sb.aeoGeneration.startedAt;
    case 'aeo_validation': return sb.aeoValidation.startedAt;
    default: return sb.startedAt;
  }
}

export function SubBatchBlock({ sb, progressed }: Props) {
  const { numerator, denominator, provider } = fillFor(sb);
  const pct = denominator > 0 ? Math.min(100, Math.round((numerator / denominator) * 100)) : 0;
  const startedAt = phaseStartedAt(sb);
  const failedKey = sb.currentPhase === 'aeo_generation' ? sb.aeoGeneration.failed : null;
  const badge = provider ? PROVIDER_BADGE[provider] || { bg: '#F59E0B', fg: '#000', label: provider } : null;

  return (
    <div
      className={`rounded-md border bg-white shadow-sm p-2.5 transition-all duration-300 ${progressed ? 'ring-2 ring-blue-400' : ''}`}
      style={{ minHeight: 80 }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold truncate">
          {sb.siteDomain} <span className="font-mono text-gray-500">#{sb.batchNumber}</span>
        </div>
        {badge && (
          <span
            className="text-[10px] rounded px-1.5 py-0.5 font-mono"
            style={{ background: badge.bg, color: badge.fg }}
          >
            {badge.label}
          </span>
        )}
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-gray-200 overflow-hidden">
        <div
          className="h-full transition-all duration-300"
          style={{
            width: `${pct}%`,
            background: sb.status === 'failed' ? '#EF4444' : sb.currentPhase === 'waiting_for_cohort' ? '#6B7280' : '#3B82F6',
          }}
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[10px] text-gray-500 font-mono">
        <span>{numerator}/{denominator} ({pct}%)</span>
        {failedKey ? <span className="text-red-500">{failedKey} fail</span> : null}
        {startedAt && <span>{new Date(startedAt).toLocaleTimeString()}</span>}
      </div>
    </div>
  );
}
